import type { SessionUser } from './api/client'

/**
 * LE registre de navigation — la seule liste des écrans de la barre latérale.
 *
 * ─── Pourquoi un registre, et pas la liste écrite dans le composant ───
 *
 * `AppLayout` ne doit connaître AUCUN écran en particulier. Sans ce fichier, la
 * barre latérale devient l'endroit où l'on écrit « si l'utilisateur est admin,
 * afficher Organisation », puis « sauf sur cet écran », puis « mais pas pour un
 * viewer » — et le composant de chrome finit par porter les règles d'accès de
 * toute l'application, à l'endroit où personne ne les cherche.
 *
 * Ici, ajouter un écran = une vue, une route, UNE ligne ici. Le supprimer =
 * retirer les trois. C'est le même principe que `domain/registration.ts` côté
 * backend, appliqué à l'interface.
 *
 * Ce que ce registre ne contient PAS : « Mon profil » et « Paramètres de
 * l'organisation ». Ce sont des panneaux du menu du compte, pas des écrans — la
 * barre latérale liste des lieux de travail, on n'y « va » pas régler son thème.
 *
 * ─── Les deux périmètres ───
 *
 * `utilisateur` — le métier de l'application, ce que voit un client.
 * `superuser`   — l'exploitation de la PLATEFORME, réservée à l'organisation de
 *                 gestion (`organisations.is_hyperadmin`, migration 0006).
 *
 * Un seul jeu d'items est affiché à la fois : mélanger les deux dans une même
 * liste, c'est offrir à l'exploitant un menu où l'écran d'un client et l'annuaire
 * de tous les clients se ressemblent.
 */

export type Portee = 'utilisateur' | 'superuser'
export type RoleUtilisateur = SessionUser['role']

export interface EntreeNavigation {
  /**
   * Clé UNIQUE de l'entrée. Pour une entrée statique, c'est aussi le nom de la
   * route Vue Router et la clé i18n de son libellé (`nav.<nom>`).
   */
  nom: string
  /** Groupe d'affichage. Sert de clé i18n : `nav.groupe.<groupe>`. */
  groupe: string
  portee: Portee
  /**
   * Rôles autorisés. `undefined` = tous les rôles authentifiés.
   *
   * Ce filtrage est un CONFORT D'AFFICHAGE, pas une sécurité : chaque route et
   * chaque requête sont contrôlées côté serveur. Une interface qui cache un
   * bouton n'a jamais protégé une donnée — mais montrer un écran qui répondra
   * 403 est une mauvaise expérience, et fait remonter des tickets.
   */
  roles?: readonly RoleUtilisateur[]
  /**
   * Tracés du pictogramme, SANS l'enveloppe `<svg>` (posée par le composant).
   *
   * Constantes littérales : le `v-html` du composant ne voit jamais de donnée
   * extérieure. Un pictogramme venu d'une API serait une injection HTML.
   */
  icone: string
  /**
   * Où l'entrée s'affiche. `menu` (défaut) : dans son groupe, en haut de la
   * barre. `pied` : en bas de la barre, hors des groupes — pour les recours
   * (documentation, aide) qui ne sont pas des lieux de travail quotidiens et
   * ne doivent pas s'intercaler entre eux.
   */
  emplacement?: 'menu' | 'pied'
  /**
   * Libellé DÉJÀ TRADUIT, pour une entrée DYNAMIQUE (une vue définie en base) :
   * son nom vient de la base, pas des fichiers de langue. Absent : `nav.<nom>`.
   */
  libelle?: string
  /** Route à ouvrir, avec ses paramètres. Absente : la route nommée `nom`. */
  cible?: { name: string; params: Record<string, string> }
  /**
   * Autres routes sur lesquelles l'entrée est AUSSI active (avec leurs
   * sous-routes) : un conteneur de vues ouvre son premier onglet, et doit
   * rester l'entrée marquée quand on passe aux suivants.
   */
  aussiActifSur?: ReadonlyArray<{ name: string; params: Record<string, string> }>
}

export const NAVIGATION: readonly EntreeNavigation[] = [
  {
    // Au pied, avec « Développeurs » : un outil d'intégration, pas un lieu de
    // travail quotidien. Réservé aux administrateurs, MIROIR de
    // `canManageAutomatisations` (backend) — un `member` recevrait 403.
    nom: 'automatisations',
    groupe: 'general',
    portee: 'utilisateur',
    emplacement: 'pied',
    roles: ['owner', 'admin'],
    icone: '<path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" />',
  },
  {
    // Supervision des modifications de structure (dont celles des agents IA)
    // et retour arrière : un recours d'administrateur, pas un lieu de travail
    // quotidien — au pied de la barre, avec les autres outils. MIROIR de
    // `canManageStructure` (backend).
    nom: 'structure',
    groupe: 'general',
    portee: 'utilisateur',
    emplacement: 'pied',
    roles: ['owner', 'admin'],
    icone: '<path d="M3 12a9 9 0 1 0 3-6.7" /><path d="M3 4v5h5" /><path d="M12 7v5l3 2" />',
  },
  {
    // Au pied de la barre : la documentation est un recours, là où on la
    // cherche quand le reste n'a pas suffi. Aucun filtre de rôles : documenter
    // ne protège rien, et un viewer a autant besoin de l'API qu'un admin.
    nom: 'developpeurs',
    groupe: 'general',
    portee: 'utilisateur',
    emplacement: 'pied',
    icone: '<path d="m16 18 6-6-6-6" /><path d="m8 6-6 6 6 6" />',
  },

  // ---- Périmètre PLATEFORME ------------------------------------------------
  {
    nom: 'superuser.organisations',
    groupe: 'plateforme',
    portee: 'superuser',
    icone: '<path d="M3 21h18" /><path d="M5 21V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v16" /><path d="M15 21V9a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v12" /><line x1="8" y1="8" x2="10" y2="8" /><line x1="8" y1="12" x2="10" y2="12" /><line x1="8" y1="16" x2="10" y2="16" />',
  },
  {
    nom: 'superuser.plans',
    groupe: 'plateforme',
    portee: 'superuser',
    icone: '<path d="M20.59 13.41 11 3H4v7l9.59 9.59a2 2 0 0 0 2.82 0l4.18-4.18a2 2 0 0 0 0-2.82z" /><circle cx="7.5" cy="7.5" r="1.5" />',
  },
  {
    nom: 'superuser.services',
    groupe: 'plateforme',
    portee: 'superuser',
    icone: '<rect x="2" y="4" width="20" height="16" rx="2" /><path d="m2 7 10 6 10-6" />',
  },
  {
    nom: 'superuser.apparence',
    groupe: 'plateforme',
    portee: 'superuser',
    icone: '<circle cx="13.5" cy="6.5" r="1" /><circle cx="17.5" cy="10.5" r="1" /><circle cx="8.5" cy="7.5" r="1" /><circle cx="6.5" cy="12.5" r="1" /><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.93 0 1.65-.75 1.65-1.69 0-.44-.18-.84-.44-1.13-.29-.29-.44-.65-.44-1.13a1.64 1.64 0 0 1 1.67-1.67h2c3.05 0 5.55-2.5 5.55-5.55C21.97 6.01 17.46 2 12 2z" />',
  },
]

/**
 * L'organisation de l'utilisateur est-elle celle de gestion, et son rôle
 * suffisant ?
 *
 * MIROIR de `canUseSuperuser` (backend `core/org/rules.ts`). Le serveur reste
 * l'autorité : cette fonction ne sert qu'à décider d'AFFICHER la bascule et à
 * éviter d'ouvrir un écran qui répondrait 403. Toute modification de la règle se
 * répercute des deux côtés — sinon l'un cache ce que l'autre autorise
 * (frustration), ou l'inverse (l'utilisateur découvre un écran en erreur).
 */
export function peutUtiliserSuperuser(utilisateur: SessionUser | null): boolean {
  if (utilisateur === null) return false
  if (!utilisateur.isHyperadmin) return false
  return utilisateur.role === 'owner' || utilisateur.role === 'admin'
}

/** Les entrées d'une liste visibles pour un utilisateur, dans un périmètre. */
function filtrerVisibles(
  liste: readonly EntreeNavigation[],
  utilisateur: SessionUser | null,
  portee: Portee,
): EntreeNavigation[] {
  if (utilisateur === null) return []
  // Le périmètre plateforme n'existe pas pour qui n'y a pas droit : le demander
  // explicitement ne doit pas suffire à en obtenir les items.
  if (portee === 'superuser' && !peutUtiliserSuperuser(utilisateur)) return []

  return liste.filter(
    (entree) =>
      entree.portee === portee &&
      (entree.roles === undefined || entree.roles.includes(utilisateur.role)),
  )
}

/** Entrées STATIQUES visibles pour un utilisateur, dans un périmètre donné. */
export function entreesVisibles(
  utilisateur: SessionUser | null,
  portee: Portee,
): EntreeNavigation[] {
  return filtrerVisibles(NAVIGATION, utilisateur, portee)
}

export interface GroupeNavigation {
  nom: string
  entrees: EntreeNavigation[]
}

/**
 * Entrées visibles, regroupées, dans l'ordre de DÉCLARATION du registre, puis
 * les entrées DYNAMIQUES (vues définies en base) dans l'ordre fourni.
 *
 * L'ordre vient du fichier et non d'un tri alphabétique : « Tableau de bord »
 * en tête est un choix éditorial, qu'un tri par nom casserait à la première
 * traduction. Les entrées dynamiques passent par le MÊME filtre de périmètre
 * et de rôle que les statiques.
 */
export function groupesVisibles(
  utilisateur: SessionUser | null,
  portee: Portee,
  dynamiques: readonly EntreeNavigation[] = [],
): GroupeNavigation[] {
  const groupes: GroupeNavigation[] = []
  const entrees = [
    ...entreesVisibles(utilisateur, portee),
    ...filtrerVisibles(dynamiques, utilisateur, portee),
  ]
  for (const entree of entrees) {
    if (entree.emplacement === 'pied') continue
    let groupe = groupes.find((g) => g.nom === entree.groupe)
    if (groupe === undefined) {
      groupe = { nom: entree.groupe, entrees: [] }
      groupes.push(groupe)
    }
    groupe.entrees.push(entree)
  }
  return groupes
}

/** Entrées visibles du pied de la barre latérale, dans l'ordre de déclaration. */
export function entreesDePied(
  utilisateur: SessionUser | null,
  portee: Portee,
): EntreeNavigation[] {
  return entreesVisibles(utilisateur, portee).filter((entree) => entree.emplacement === 'pied')
}

/**
 * Route d'atterrissage du périmètre utilisateur. Il n'a plus d'écran statique
 * dans le menu : ses lieux de travail sont les vues définies en base, connues
 * seulement à l'exécution. `accueil` attend leur liste et ouvre la première.
 */
export const ACCUEIL = 'accueil'

/**
 * Page d'atterrissage d'un périmètre.
 *
 * Utilisé au changement de périmètre : rester sur l'écran précédent laisserait
 * une barre latérale sans item actif, et l'utilisateur devant un contenu qui
 * n'appartient plus au menu affiché. Le pied est écarté : la documentation est
 * un recours, pas l'endroit où l'on arrive.
 */
export function accueilDuPerimetre(
  utilisateur: SessionUser | null,
  portee: Portee,
): string | null {
  if (portee === 'utilisateur') return utilisateur === null ? null : ACCUEIL
  return (
    entreesVisibles(utilisateur, portee).find((entree) => entree.emplacement !== 'pied')?.nom ??
    null
  )
}

/**
 * Routes des entrées DYNAMIQUES et leur périmètre. Elles ne figurent pas dans
 * `NAVIGATION` (une entrée par vue, connue à l'exécution) ; sans cette table, un
 * lien profond vers une vue ouvert depuis le périmètre plateforme garderait une
 * barre latérale qui ne la contient pas.
 */
const ROUTES_DYNAMIQUES: Readonly<Record<string, Portee>> = {
  [ACCUEIL]: 'utilisateur',
  vue: 'utilisateur',
  'vue.nouvelle': 'utilisateur',
  'vue.fiche': 'utilisateur',
}

/** Périmètre auquel appartient une route, `null` si elle n'est pas dans la nav. */
export function porteeDeLaRoute(nomRoute: string | null | undefined): Portee | null {
  if (typeof nomRoute !== 'string') return null
  return (
    NAVIGATION.find((entree) => entree.nom === nomRoute)?.portee ??
    ROUTES_DYNAMIQUES[nomRoute] ??
    null
  )
}

/**
 * Pictogramme d'un écran du registre, pour son en-tête : le MÊME tracé que dans
 * la barre latérale, sans recopie — sinon l'écran et son entrée de menu
 * finiraient par montrer deux dessins différents. `undefined` pour une route
 * hors registre : l'en-tête s'affiche alors sans pictogramme.
 */
export function iconeDeRoute(nomRoute: string): string | undefined {
  return NAVIGATION.find((entree) => entree.nom === nomRoute)?.icone
}
